import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  Image as ImageIcon,
  Layers,
  ArrowLeft,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Edit,
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import {
  useUpdateBannerDisplayOrderMutation,
  useUpdateBannerStatusMutation,
  useLazyGetBannerDashboardQuery,
} from "../../../redux/actions/Admin/ProductAdmin";

const AdminBannerInnerSection: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { screenId, sectionId, sectionData } = location.state || {
    screenId: null,
    sectionId: null,
    sectionData: null,
  };

  /**
   * Keep the section passed from the previous screen as the
   * initial value, then replace it with the latest dashboard
   * data fetched from the API.
   */
  const [currentSectionData, setCurrentSectionData] =
    useState<any>(sectionData);

  // ------------------------------------------------------------
  // RTK QUERY / MUTATIONS
  // ------------------------------------------------------------

  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateBannerStatusMutation();

  const [updateOrder, { isLoading: isUpdatingOrder }] =
    useUpdateBannerDisplayOrderMutation();

  const [triggerRefreshDashboard, { isFetching: isRefreshingTree }] =
    useLazyGetBannerDashboardQuery();

  // ------------------------------------------------------------
  // LOAD / REFRESH BANNER SECTION
  // ------------------------------------------------------------

  const refreshBannerSection = async () => {
    if (!screenId || !sectionId) {


      return;
    }

    try {
      const dashboard = await triggerRefreshDashboard(
        Number(screenId),
      ).unwrap();

      /**
       * Dashboard response:
       *
       * {
       *   screenId,
       *   screenName,
       *   sections: [
       *     {
       *       sectionId,
       *       displayName,
       *       banners: [...]
       *     }
       *   ]
       * }
       */

      const sections = dashboard?.sections || [];

      const bannerSection = sections.find(
        (section: any) => Number(section.sectionId) === Number(sectionId),
      );

      if (bannerSection) {
        setCurrentSectionData(bannerSection);
      } else {
       
        /**
         * Keep the sectionData passed through navigation
         * as fallback if the API doesn't contain the section.
         */
        if (sectionData) {
          setCurrentSectionData(sectionData);
        }
      }
    } catch (error) {
      console.error("Failed to load/refresh banner section", error);

      /**
       * If API fails, keep whatever was passed through
       * navigation so the page doesn't become completely empty.
       */
      if (sectionData) {
        setCurrentSectionData(sectionData);
      }
    }
  };

  // ------------------------------------------------------------
  // INITIAL LOAD
  // ------------------------------------------------------------

  useEffect(() => {
    const loadBannerSection = async () => {
      if (!screenId || !sectionId) {
        return;
      }

      try {
        const dashboard = await triggerRefreshDashboard(
          Number(screenId),
        ).unwrap();

        const bannerSection = dashboard?.sections?.find(
          (section: any) => Number(section.sectionId) === Number(sectionId),
        );

        if (bannerSection) {
          setCurrentSectionData(bannerSection);
        } else if (sectionData) {
          setCurrentSectionData(sectionData);
        }
      } catch (error) {
        console.error("Failed to load banner section", error);

        if (sectionData) {
          setCurrentSectionData(sectionData);
        }
      }
    };

    loadBannerSection();
  }, [screenId, sectionId, triggerRefreshDashboard, sectionData]);

  // ------------------------------------------------------------
  // TOGGLE ACTIVE / INACTIVE
  // ------------------------------------------------------------

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await updateStatus({
        id,
        data: {
          isActive: !currentStatus,
        },
      }).unwrap();

      await refreshBannerSection();
    } catch (err) {
      console.error("Failed to update banner status", err);
    }
  };

  // ------------------------------------------------------------
  // DRAG & DROP
  // ------------------------------------------------------------

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetDisplayOrder: number) => {
    const draggedBannerId = Number(e.dataTransfer.getData("bannerId"));

    if (!draggedBannerId) {
      return;
    }

    try {
      await updateOrder({
        id: draggedBannerId,
        data: {
          bannerId: draggedBannerId,
          displayOrder: targetDisplayOrder,
        },
      }).unwrap();

      await refreshBannerSection();
    } catch (err) {
      console.error("Failed to change banner order", err);
    }
  };

  // ------------------------------------------------------------
  // BANNERS
  // ------------------------------------------------------------

  const bannersList = currentSectionData?.banners || [];

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------

  return (
    <div className="min-h-screen max-w-7xl mx-auto px-4 py-6 space-y-6 text-left">
      {/* ============================================================
          HEADER ACTIONS
      ============================================================ */}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-5 border-b border-slate-100 gap-4">
        <div className="space-y-1">
          <button
            type="button"
            onClick={() =>
              navigate("/admin/uiconfigure/sections", {
                state: {
                  screenId,
                },
              })
            }
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 px-3 py-1.5 rounded-xl hover:bg-slate-50 shadow-3xs cursor-pointer mb-2"
          >
            <ArrowLeft size={13} className="stroke-[2.5]" />

            <span>Back to Sections</span>
          </button>

          <h1 className="text-xl font-black text-gray-900 tracking-tight">
            {currentSectionData?.displayName ||
              sectionData?.displayName ||
              "Section Banners"}
          </h1>

          <p className="text-xs text-slate-400 font-semibold max-w-2xl">
            {currentSectionData?.description ||
              sectionData?.description ||
              "Manage banners deployed inside this app section."}
          </p>
        </div>

        <CommonButton
          icon={<Plus size={15} />}
          iconPosition="left"
          text="Create New Banner"
          onClick={() =>
            navigate("adCreateNewBanner", {
              state: {
                screenId,
                screenSectionId: sectionId,

                config: {
                  desktopWidth:
                    currentSectionData?.preview?.desktopWidth || 1920,

                  desktopHeight:
                    currentSectionData?.preview?.desktopHeight || 600,

                  mobileHeight:
                    currentSectionData?.preview?.mobileHeight || 3.2,

                  mobileWidth: currentSectionData?.preview?.mobileWidth || 1000,
                },
              },
            })
          }
          variant="filled"
          className="bg-[#2F5E2E] text-white font-black text-xs px-5 h-10 shadow-3xs rounded-xl hover:bg-[#1f3f1e]"
        />
      </div>

      {/* ============================================================
          SYNC / LOADING BAR
      ============================================================ */}

      {(isUpdatingStatus || isUpdatingOrder || isRefreshingTree) && (
        <div className="bg-emerald-50 text-[#2F5E2E] border border-emerald-100 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" />

          <span>
            {isRefreshingTree
              ? "Loading banners..."
              : "Saving changes to server..."}
          </span>
        </div>
      )}

      {/* ============================================================
          BANNERS INVENTORY
      ============================================================ */}

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-3xs overflow-hidden">
        {/* ----------------------------------------------------------
            SECTION HEADER
        ---------------------------------------------------------- */}

        <div className="flex justify-between items-center px-5 py-4 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Layers size={16} className="text-[#2F5E2E]" />

            <h2 className="text-xs font-black uppercase tracking-wider text-slate-700">
              Active Banners
            </h2>

            <span className="text-[10px] font-bold bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full ml-1">
              {bannersList.length} Total
            </span>
          </div>
        </div>

        {/* ----------------------------------------------------------
            BANNER LIST
        ---------------------------------------------------------- */}

        <div className="p-4 sm:p-6 space-y-4 bg-white">
          {bannersList.length === 0 ? (
            <div className="border border-dashed border-slate-200 rounded-xl p-16 text-center text-xs font-bold text-slate-400 italic">
              No banners uploaded in this section yet. Click "Create New Banner"
              above to start.
            </div>
          ) : (
            <>
              <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">
                💡 Tip: Drag and drop cards vertically to change their display
                order on the app.
              </p>

              {bannersList.map((ad: any) => (
                <div
                  key={ad.id}
                  draggable
                  onDragStart={(e) =>
                    e.dataTransfer.setData("bannerId", String(ad.id))
                  }
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, ad.displayOrder)}
                  className={`group rounded-xl border p-4 bg-slate-50/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition hover:border-[#6DBE45] cursor-grab active:cursor-grabbing ${
                    ad.isActive
                      ? "border-slate-200"
                      : "opacity-50 bg-slate-100/40"
                  }`}
                >
                  {/* ------------------------------------------------
                        BANNER INFO
                    ------------------------------------------------ */}

                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* IMAGE */}

                    <div className="w-28 h-14 sm:w-36 sm:h-16 bg-slate-900 border border-slate-200 rounded-lg overflow-hidden shrink-0 shadow-3xs relative flex items-center justify-center">
                      {ad.imageUrl ? (
                        <img
                          src={ad.imageUrl}
                          alt={ad.title || "Banner"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon size={16} className="text-slate-600" />
                      )}
                    </div>

                    {/* TITLE / META */}

                    <div className="min-w-0 space-y-0.5">
                      <h4 className="text-sm font-black text-slate-900 truncate">
                        {ad.title || "Untitled Banner"}
                      </h4>

                      <p className="text-[11px] text-slate-400 font-semibold flex items-center gap-2">
                        <span>
                          Position Order:{" "}
                          <span className="font-mono font-bold text-slate-700">
                            {ad.displayOrder}
                          </span>
                        </span>

                        {ad.targetType && (
                          <>
                            <span>•</span>

                            <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[9px] font-mono text-slate-600">
                              Action: {ad.targetType}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* ------------------------------------------------
                        ACTIVE / INACTIVE
                    ------------------------------------------------ */}

                  <div className="flex items-center gap-3 shrink-0 self-end sm:self-center border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-100">
                    <span
                      className={`text-[10px] font-black uppercase tracking-wider ${
                        ad.isActive ? "text-emerald-700" : "text-slate-400"
                      }`}
                    >
                      {ad.isActive ? "Live" : "Paused"}
                    </span>

                    <button
                      type="button"
                      onClick={() => handleToggleActive(ad.id, ad.isActive)}
                      className="text-slate-700 transition focus:outline-none cursor-pointer"
                    >
                      {ad.isActive ? (
                        <ToggleRight className="w-8 h-8 text-[#2F5E2E] stroke-[1.5]" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-slate-300 stroke-[1.5]" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        navigate("adCreateNewBanner", {
                          state: {
                            mode: "edit",
                            bannerId: ad.id,
                            screenId,
                            screenSectionId: sectionId,
                            config: {
                              desktopWidth:
                                currentSectionData?.preview?.desktopWidth ||
                                1920,
                              desktopHeight:
                                currentSectionData?.preview?.desktopHeight ||
                                600,
                              mobileWidth:
                                currentSectionData?.preview?.mobileWidth ||
                                1000,
                              mobileHeight:
                                currentSectionData?.preview?.mobileHeight ||
                                1000,
                            },
                          },
                        })
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-bold hover:bg-slate-50 hover:border-[#6DBE45] transition cursor-pointer"
                      title="Edit banner"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBannerInnerSection;
